import Phaser from "phaser";
import { isMobile } from "../utils/mobile-utils";

// Type definition for tileObjectsBlockMap
declare global {
  interface Window {
    tileObjectsBlockMap?: {
      width: number;
      height: number;
      tilewidth: number;
      tileheight: number;
      data: number[];
    };
  }
}

export type AttackAnimation = {
  attackerX: number;
  attackerY: number;
  targetX: number;
  targetY: number;
  color: number;
  startTime: number;
  duration: number;
  graphics: Phaser.GameObjects.Graphics;
};

// Fix TypeScript error for attack event data
export type AttackEventData = {
  attackerId: string;
  attackerX: number;
  attackerY: number;
  targetX: number;
  targetY: number;
  hit: boolean;
  timestamp: number;
  blocked?: boolean;
  range?: number;
};

export class AttackAnimationManager {
  private scene: Phaser.Scene;
  private animations: AttackAnimation[] = [];
  // Reduce segments on mobile (6 on mobile, 15 on desktop) for better performance
  private readonly numSegments = isMobile() ? 6 : 15;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  addAttackAnimation(data: {
    attackerX: number;
    attackerY: number;
    targetX: number;
    targetY: number;
    color: number;
    duration?: number;
  }) {
    // Guard: Check if scene is valid and active before creating graphics
    if (!this.scene || !this.scene.add || !this.scene.scene.isActive()) {
      return;
    }

    const graphics = this.scene.add.graphics();
    graphics.setDepth(2); // Render above tile-effects layer (depth 1)

    // Ensure attack lines are only visible in the main camera, not the UI camera
    const cameras = this.scene.cameras.cameras;
    cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
      if (camera !== this.scene.cameras.main) {
        camera.ignore(graphics);
      }
    });

    this.animations.push({
      ...data,
      startTime: this.scene.time.now,
      duration: data.duration ?? 250,
      graphics,
    });
  }

  update() {
    // Guard: Check if scene is valid and active
    if (!this.scene || !this.scene.time || !this.scene.scene.isActive()) {
      return;
    }

    const now = this.scene.time.now;
    this.animations = this.animations.filter((anim) => {
      const elapsed = now - anim.startTime;
      if (elapsed > anim.duration) {
        anim.graphics.destroy();
        return false;
      }
      let t = elapsed / anim.duration;
      const dx = anim.targetX - anim.attackerX;
      const dy = anim.targetY - anim.attackerY;
      const fullLen = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);
      let startX = anim.attackerX;
      let startY = anim.attackerY;
      let endX = anim.targetX;
      let endY = anim.targetY;

      // The server already determines the hit position (player, enemy, or obstacle).
      // Always use the provided targetX/targetY as the endpoint for the animation.

      const blockedLen = Math.sqrt(
        (endX - anim.attackerX) ** 2 + (endY - anim.attackerY) ** 2,
      );
      if (t < 0.4) {
        // Grow line from start to end
        let lengthFrac = t / 0.4;
        lengthFrac = Phaser.Math.Clamp(lengthFrac, 0, 1);
        endX = anim.attackerX + Math.cos(angle) * blockedLen * lengthFrac;
        endY = anim.attackerY + Math.sin(angle) * blockedLen * lengthFrac;
      } else {
        // Slide start point toward end point
        let slideFrac = (t - 0.4) / 0.6;
        slideFrac = Phaser.Math.Clamp(slideFrac, 0, 1);
        startX = anim.attackerX + Math.cos(angle) * blockedLen * slideFrac;
        startY = anim.attackerY + Math.sin(angle) * blockedLen * slideFrac;
      }
      anim.graphics.clear();
      const lineLength = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2);

      if (lineLength > 0) {
        for (let i = 0; i < this.numSegments; i++) {
          const t1 = i / this.numSegments;
          const t2 = (i + 1) / this.numSegments;

          const segStartX = startX + (endX - startX) * t1;
          const segStartY = startY + (endY - startY) * t1;
          const segEndX = startX + (endX - startX) * t2;
          const segEndY = startY + (endY - startY) * t2;

          // Front edge (closer to endX/endY) is opaque, back edge is transparent
          const alpha = t2; // 0 at back, 1 at front

          anim.graphics.lineStyle(3, anim.color, alpha);
          anim.graphics.beginPath();
          anim.graphics.moveTo(segStartX, segStartY);
          anim.graphics.lineTo(segEndX, segEndY);
          anim.graphics.strokePath();
        }
      }

      return true;
    });
  }
}

// Utility to spawn a random blood splatter at (x, y) with direction (dx, dy)
export function spawnBloodSplatter(
  scene: Phaser.Scene,
  x: number,
  y: number,
  splatterArray: Array<{
    graphics: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite;
    createdAt: number;
  }>,
) {
  const key = "blood_" + Math.random().toString(36).substr(2, 9);
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.clearRect(0, 0, 32, 32);
    // Calculate spray direction
    let sprayAngle = Math.random() * Math.PI * 2;
    // More pixels overall for larger canvas
    const totalPixels = 80;
    for (let i = 0; i < totalPixels; i++) {
      // Bias: more pixels at start, fewer at end
      // t=0 (start) to t=1 (end), density falls off
      const t = Math.pow(i / totalPixels, 0.7); // slower falloff at start
      // Spray length: longer splatter
      const radius = 4 + t * 22 + Math.random() * 2;
      // Angle is biased toward sprayAngle, with some random spread
      const angle = sprayAngle + ((Math.random() - 0.5) * Math.PI) / 6;
      const px = Math.floor(
        16 + Math.cos(angle) * radius + (Math.random() - 0.5) * 2,
      );
      const py = Math.floor(
        16 + Math.sin(angle) * radius + (Math.random() - 0.5) * 2,
      );
      if (px >= 0 && px < 32 && py >= 0 && py < 32) {
        // Opacity and color: more intense at start
        const r = Math.floor(120 + Math.random() * 80);
        const g = Math.floor(Math.random() * 30);
        const b = Math.floor(Math.random() * 30);
        const alpha = 0.8 - t * 0.5 + Math.random() * 0.2;
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.fillRect(px, py, 1, 1);
      }
    }
  }
  scene.textures.addCanvas(key, canvas);
  // The spray is drawn horizontally, so rotate by angle + Math.PI/2 to align with attack direction
  const sprite = scene.add.sprite(x, y, key);
  sprite.setOrigin(0.5);
  sprite.setRotation(Math.random() * Math.PI * 2 + Math.PI / 2);
  sprite.setScale(1.5);
  sprite.setDepth(0); // Ensure blood splatter is beneath other sprites

  // Ensure blood splatter is only visible in the main camera, not the UI camera
  const cameras = scene.cameras.cameras;
  cameras.forEach((camera: Phaser.Cameras.Scene2D.Camera) => {
    if (camera !== scene.cameras.main) {
      camera.ignore(sprite);
    }
  });

  splatterArray.push({ graphics: sprite, createdAt: Date.now() });
}
